import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as RT from '@radix-ui/react-tooltip';
import { cn } from '../../lib/cn';

/**
 * Tooltip primitive (Compose API over Radix UI).
 *
 * `TooltipProvider` is exported separately so the consumer can mount it once
 * near the app root with custom delays. `Tooltip` is the per-instance Root.
 *
 * Usage:
 * ```tsx
 * <TooltipProvider delayDuration={150}>
 *   <Tooltip>
 *     <TooltipTrigger asChild><Button>?</Button></TooltipTrigger>
 *     <TooltipContent>Hello</TooltipContent>
 *   </Tooltip>
 * </TooltipProvider>
 * ```
 */
export const TooltipProvider = RT.Provider;
export const Tooltip = RT.Root;
export const TooltipTrigger = RT.Trigger;

export const TooltipContent = forwardRef<
  ElementRef<typeof RT.Content>,
  ComponentPropsWithoutRef<typeof RT.Content>
>(function TooltipContent({ className, sideOffset = 6, ...props }, ref) {
  return (
    <RT.Portal>
      <RT.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'z-50 overflow-hidden rounded-md border border-border bg-bg-card px-2 py-1 text-xs ' +
            'text-white shadow-lg animate-fade-in',
          className,
        )}
        {...props}
      />
    </RT.Portal>
  );
});
